import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import {
  provideTranslocoScope,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { MzicSvgComponent, ToastComponent } from '@mzic/mzic-components';
import { CollectionWorkflowTargetDto } from '../../../interfaces/collection-revision.interface';
import { RevisionTargetShare } from '../../../interfaces/collection.interface';
import { ShareInfoDialogComponent } from '../../share-info-dialog/share-info-dialog.component';

type TargetContext = 'ecad' | 'streaming' | 'mechanical' | 'default';

@Component({
  selector: 'lib-pending-shares-step',
  imports: [CommonModule, TranslocoModule, MzicSvgComponent, ShareInfoDialogComponent, ToastComponent],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './pending-shares-step.component.html',
  styleUrl: './pending-shares-step.component.scss',
})
export class PendingSharesStepComponent {
  private readonly translocoService = inject(TranslocoService);

  @Input() partnerName = '';
  @Input() integrationEnabled = false;
  @Input() integrationEntityName = '';
  @Input() shares: readonly RevisionTargetShare[] = [];
  @Input() targets: readonly CollectionWorkflowTargetDto[] = [];
  @Input() loading = false;
  @Input() collectionId = '';
  @Output() closeRequested = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();
  @Output() revokeRequested = new EventEmitter<string>();
  @Output() allSharesRevoked = new EventEmitter<void>();

  // Share info dialog state
  selectedShare = signal<RevisionTargetShare | null>(null);
  showShareInfoDialog = signal(false);

  // Toast state
  toastVisible = signal(false);
  toastMessage = signal('');
  toastState = signal<'error' | 'success' | 'alert'>('success');

  get shimmerItems(): number[] {
    return [0, 1, 2];
  }

  get integrationSuffix(): string {
    return this.integrationEnabled ? '_integration' : '';
  }

  get pendingSharesDescriptionKey(): string {
    return `lang.pending_shares_description${this.integrationSuffix}`;
  }

  onClose(): void {
    this.closeRequested.emit();
  }

  onBack(): void {
    this.back.emit();
  }

  onShareInfoClick(share: RevisionTargetShare): void {
    this.selectedShare.set(share);
    this.showShareInfoDialog.set(true);
  }

  onShareInfoClose(): void {
    this.showShareInfoDialog.set(false);
    this.selectedShare.set(null);
  }

  onShareRevokeRequested(shareId: string): void {
    if (!this.loading) this.revokeRequested.emit(shareId);
  }

  /**
   * Called by parent after shares are refreshed.
   * Checks if list is now empty and emits allSharesRevoked if so.
   */
  checkAllSharesRevoked(): void {
    if (this.shares.length === 0) {
      this.allSharesRevoked.emit();
    }
  }

  showSuccessToast(): void {
    this.toastMessage.set(
      this.translocoService.translate(
        'share_revoked_success',
        {},
        'mzic-collection-flow',
      ),
    );
    this.toastState.set('success');
    this.toastVisible.set(true);
  }

  showAlertToast(message: string): void {
    this.toastMessage.set(message);
    this.toastState.set('alert');
    this.toastVisible.set(true);
  }

  dismissToast(): void {
    this.toastVisible.set(false);
  }

  getShareUrl(): string {
    // Construct the share URL - this is the collection URL
    // The share is accessed via the same collection URL with passcode
    return `${window.location.origin}/collection/${this.collectionId}`;
  }

  /**
   * Determines the context based on revenue type and subtype
   * Same logic as file-upload-step
   */
  private getTargetContext(target: CollectionWorkflowTargetDto): TargetContext {
    const type = target.revenue_type?.toUpperCase();
    const subtype = target.revenue_subtype?.toUpperCase();

    // ECAD: Sociedades de gestão coletiva (collective management societies)
    if (type === 'PUBLIC_PERFORMANCE' && subtype === 'EXECUTION') {
      return 'ecad';
    }

    // Streaming platforms (distributors)
    if (type === 'STREAMING') {
      return 'streaming';
    }

    // Mechanical rights
    if (type === 'MECHANICAL' || subtype === 'MECHANICAL') {
      return 'mechanical';
    }

    return 'default';
  }

  /**
   * Gets the display name for a share using semantic naming
   * Uses translation keys based on target context
   */
  getTargetDisplayName(share: RevisionTargetShare): string {
    const targetInfo = share.target_ids
      .map((id) => {
        const target = this.targets.find((candidate) => candidate.target_id === id);
        if (target) {
          const context = this.getTargetContext(target);
          return this.translocoService.translate(
            `share_target_type_${context}${this.integrationSuffix}`,
            {},
            'mzic-collection-flow',
          );
        }
        return null;
      })
      .filter(Boolean);

    return (
      targetInfo.join(', ') ||
      this.translocoService.translate(
        `share_target_type_default${this.integrationSuffix}`,
        {},
        'mzic-collection-flow',
      )
    );
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Internationalize date based on active language (like share-info-dialog)
    const locale =
      this.translocoService.getActiveLang()?.toLowerCase() === 'pt-br'
        ? 'pt-BR'
        : 'en-US';
    return date.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
}
